import React, { useState, useCallback, useRef } from 'react';
import MobileTableView from './MobileTableView';
import DesktopTableView from './DesktopTableView';
import SearchFilters from '../shared/SearchFilters';
import ModelSelector from '../shared/ModelSelector';
import useDataFiltering from '../../hooks/useDataFiltering';

/**
 * Main table view component that handles data display and filtering
 * 
 * @param {Object} props - Component props
 * @param {Array} props.benchmarkData - Benchmark data array
 * @param {Array} props.modelColumns - Array of model objects
 * @param {Object} props.averageScores - Object containing average scores
 * @param {boolean} props.isMobileView - Whether mobile view is active
 * @param {Array} props.visibleModels - Array of currently visible models
 * @param {Function} props.getVisibleModels - Function to get visible models
 * @param {number} props.selectedModelIndex - Currently selected model index for mobile view
 * @param {Function} props.setSelectedModelIndex - Function to update selected model
 */
const TableView = ({ 
  benchmarkData,
  modelColumns,
  averageScores,
  isMobileView,
  visibleModels,
  getVisibleModels,
  selectedModelIndex,
  setSelectedModelIndex
}) => {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'ascending' });
  
  // State for mobile view modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Handle sorting
  const handleSort = useCallback((key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);
  
  // Open model scores modal
  const openModal = useCallback((item) => {
    console.log('Opening modal for item:', item.id, item.task);
    setSelectedItem(item);
    setIsModalOpen(true);
  }, []);

  // Close model scores modal
  const closeModal = useCallback(() => {
    console.log('Closing modal');
    setIsModalOpen(false);
    // Keep the selected item for a moment to prevent flash of empty content during close animation
    setTimeout(() => setSelectedItem(null), 300);
  }, []);

  // Filter data based on search and category
  const filteredData = useDataFiltering(
    benchmarkData,
    searchTerm,
    selectedCategory,
    sortConfig
  );

  return (
    <div>
      {/* Search and filter controls */}
      <SearchFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      
      {/* Mobile model selector */}
      {isMobileView && (
        <>
          <ModelSelector
            models={modelColumns}
            selectedIndex={selectedModelIndex}
            setSelectedIndex={setSelectedModelIndex}
          />
          <div className="text-xs text-center text-gray-500 mb-2">
            Swipe left/right to change models
          </div>
        </>
      )}

      {/* Render either mobile or desktop table view */}
      {isMobileView ? (
        <MobileTableView
          filteredData={filteredData}
          visibleModels={visibleModels}
          modelColumns={modelColumns}
          openModal={openModal}
          isModalOpen={isModalOpen}
          selectedItem={selectedItem}
          closeModal={closeModal}
          averageScores={averageScores}
        />
      ) : (
        <DesktopTableView
          filteredData={filteredData}
          visibleModels={visibleModels}
          sortConfig={sortConfig}
          handleSort={handleSort}
          averageScores={averageScores}
        />
      )}
      
      {/* Footnotes */}
      <div className="mt-4 text-xs text-gray-500 text-center space-y-1">
        <div>* Data source: MMLU Benchmarks for AI model performance comparison</div>
        <div>Color coding: Darker blue = higher scores (better performance), lighter blue = lower scores. 
        This heatmap ranges from dark blue (≥95%) to light blue (below 60%).</div>
      </div>
    </div>
  );
};

export default TableView;
